import { useState, useEffect, useCallback } from "react";
import { fetchUserProfile } from "../contexts/AuthContext";
import config from "../config";

// default cooldown in case backend doesn't provide it
const DEFAULT_COOLDOWN_DAYS = 60;

// validation helpers
const sanitize = (str) => {
  if (!str) return "";
  return str.trim().replace(/[<>]/g, "");
};

const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const isValidPhone = (phone) => {
  if (!phone) return true; // optional field
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};

// cooldown keys stored in localStorage
const COOLDOWN_KEY = "profile_field_cooldowns";

const getCooldowns = () => {
  try {
    return JSON.parse(localStorage.getItem(COOLDOWN_KEY) || "{}");
  } catch {
    return {};
  }
};

const setCooldown = (field) => {
  const cooldowns = getCooldowns();
  cooldowns[field] = Date.now();
  localStorage.setItem(COOLDOWN_KEY, JSON.stringify(cooldowns));
};

const isOnCooldown = (field, cooldownDays) => {
  const cooldowns = getCooldowns();
  const lastChange = cooldowns[field];
  if (!lastChange) return false;
  const daysSince = (Date.now() - lastChange) / (1000 * 60 * 60 * 24);
  return daysSince < cooldownDays;
};

const getDaysRemaining = (field, cooldownDays) => {
  const cooldowns = getCooldowns();
  const lastChange = cooldowns[field];
  if (!lastChange) return 0;
  const daysSince = (Date.now() - lastChange) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.ceil(cooldownDays - daysSince));
};

export const useProfileData = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  // cooldown days from backend policy (with fallback)
  const [cooldownDays, setCooldownDays] = useState(DEFAULT_COOLDOWN_DAYS);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const result = await fetchUserProfile();
        if (result) {
          const p = result.profile;
          if (p?.profile_pic_url?.startsWith("/api/")) {
            p.profile_pic_url = `${config.apiUrl}${p.profile_pic_url.slice(4)}`;
          }
          setProfile(p);
          if (result.policy?.cooldown_days) {
            setCooldownDays(result.policy.cooldown_days);
          }
        } else {
          setProfile(null);
        }
      } catch (error) {
        console.error("Failed to load profile:", error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  // validate fields before submission
  const validateFields = useCallback(
    (data, originalProfile) => {
      const errors = [];

      // check email
      if (data.email !== undefined) {
        if (!isValidEmail(data.email)) {
          errors.push("Invalid email format");
        }
        // check cooldown only if email changed
        if (
          data.email !== originalProfile?.email &&
          isOnCooldown("email", cooldownDays)
        ) {
          errors.push(
            `Email can only be changed once every ${cooldownDays} days. ${getDaysRemaining("email", cooldownDays)} days remaining.`,
          );
        }
      }

      // check phone
      if (data.phone_number !== undefined && !isValidPhone(data.phone_number)) {
        errors.push("Invalid phone number format");
      }

      // check address cooldown
      if (
        data.address !== undefined &&
        data.address !== originalProfile?.address
      ) {
        if (isOnCooldown("address", cooldownDays)) {
          errors.push(
            `Address can only be changed once every ${cooldownDays} days. ${getDaysRemaining("address", cooldownDays)} days remaining.`,
          );
        }
      }

      // check company name cooldown
      if (
        data.company_name !== undefined &&
        data.company_name !== originalProfile?.company_name
      ) {
        if (isOnCooldown("company_name", cooldownDays)) {
          errors.push(
            `Company name can only be changed once every ${cooldownDays} days. ${getDaysRemaining("company_name", cooldownDays)} days remaining.`,
          );
        }
      }

      return errors;
    },
    [cooldownDays],
  );

  // update profile with validation
  const updateProfile = useCallback(
    async (data) => {
      const errors = validateFields(data, profile);
      if (errors.length > 0) {
        return { success: false, errors };
      }

      // sanitize all string fields
      const sanitized = {
        email: sanitize(data.email),
        phone_number: sanitize(data.phone_number),
        company_name: sanitize(data.company_name),
        address: sanitize(data.address),
        city: sanitize(data.city),
      };

      setUpdating(true);
      try {
        const res = await fetch(`${config.apiUrl}/user/profile`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify(sanitized),
        });

        const result = await res.json();

        if (res.ok && result.success) {
          // set cooldowns for changed sensitive fields
          if (sanitized.email !== profile?.email) setCooldown("email");
          if (sanitized.address !== profile?.address) setCooldown("address");
          if (sanitized.company_name !== profile?.company_name)
            setCooldown("company_name");

          // update local state
          setProfile((prev) => ({ ...prev, ...sanitized }));
          return { success: true };
        } else {
          return {
            success: false,
            errors: [result.message || "Failed to update profile"],
          };
        }
      } catch (err) {
        console.error("Profile update error:", err);
        return { success: false, errors: ["Network error. Please try again."] };
      } finally {
        setUpdating(false);
      }
    },
    [profile, validateFields],
  );

  // check if field is on cooldown (for UI)
  const getFieldCooldown = useCallback(
    (field) => {
      if (!isOnCooldown(field, cooldownDays)) return null;
      return getDaysRemaining(field, cooldownDays);
    },
    [cooldownDays],
  );

  return {
    profile,
    loading,
    updating,
    updateProfile,
    getFieldCooldown,
    cooldownDays, // expose for UI display
  };
};
